<template>
    <div class="max-w-500 mx-auto px-4 my-4" data-cy="container_financial_health_quiz">
        <!-- Loading Screen -->
        <div v-if="isLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-white" data-cy="container_financial_health_quiz_loading">
            <div class="flex flex-col items-center">
                <div class="w-12 h-12 border-4 border-a-blue-0064ff border-t-transparent rounded-full animate-spin"></div>
                <p class="mt-4 text-16 text-a-gray-666666">กำลังโหลด...</p>
            </div>
        </div>
        
        <!-- Question Pages -->
        <QuizQuestion
            v-if="!isLoading && currentPage.type === 'question'"
            data-cy="component_financial_health_quiz_question"
            :key="currentPage.id"
            :currentCategory="currentPage.category"
            :categoryIcon="currentPage.categoryIcon"
            :categoryTitle="currentPage.categoryTitle"
            :questionTitle="currentPage.questionTitle"
            :questionSubtitle="currentPage.questionSubtitle"
            :notice="currentPage.notice"
            :notice1="currentPage.notice1"
            :choices="currentPage.choices"
            :choiceFont="currentPage.choiceFont"
            :initialAnswer="answers[currentPage.id]"
            @answer="handleAnswer"
            @next="handleNext"
            @back="handleBack"
        />
        
        <!-- How-To Pages -->
        <QuizHowTo
            v-else-if="!isLoading && currentPage.type === 'howto'"
            data-cy="component_financial_health_quiz_howto"
            :currentCategory="currentPage.category"
            :image="currentPage.image"
            :title="currentPage.title"
            :description="currentPage.description"
            @next="handleNext"
            @back="handleBack"
        />
    </div>
</template>

<script>
import QuizQuestion from '../component/QuizQuestion.vue';
import QuizHowTo from '../component/QuizHowTo.vue';
import BzbsFinancialHealth from '@/core/FinancialHealth/service/BzbsFinancialHealth.js';
import ModalStore from '@/store/ModalStore.js';
import SurveyStatusHelper from '../helper/SurveyStatusHelper.js';

export default {
    name: "FinancialHealthQuiz",
    components: {
        QuizQuestion,
        QuizHowTo
    },
    data() {
        return {
            isLoading: true,
            currentPageIndex: 0,
            answers: {},
            startTime: null,
            isSubmitting: false,
            modal: null,
            // Answer mapping for API submission
            answerMapping: {
                spend_q1: { category: 'Spend', questionNumber: 1 },
                spend_q2: { category: 'Spend', questionNumber: 2 },
                save_q1: { category: 'Save', questionNumber: 1 },
                save_q2: { category: 'Save', questionNumber: 2 },
                borrow_q1: { category: 'Borrow', questionNumber: 1 },
                plan_q1: { category: 'Plan', questionNumber: 1 },
                plan_q2: { category: 'Plan', questionNumber: 2 }
            },
            pages: [
                // Page 1: Question - พฤติกรรมการใช้จ่าย Q1
                {
                    id: 'spend_q1',
                    index: 0,
                    type: 'question',
                    category: 0,
                    categoryIcon: require('@/assets/icon/healthcheck/bbl_icons_32px_creditcard.webp'),
                    categoryTitle: 'พฤติกรรมการใช้จ่าย',
                    questionTitle: 'ในช่วง 1 ปีที่ผ่านมา',
                    questionSubtitle: 'การใช้จ่ายโดยรวมของครอบครัวคุณเป็นอย่างไรเมื่อเทียบกับรายรับทั้งหมด',
                    choices: [
                        { value: 1, label: 'ใช้จ่าย **น้อยกว่า** รายรับมาก', apiAnswer: 'ใช้จ่ายน้อยกว่ารายรับมาก' },
                        { value: 2, label: 'ใช้จ่าย **น้อยกว่า** รายรับเล็กน้อย', apiAnswer: 'ใช้จ่ายน้อยกว่ารายรับเล็กน้อย' },
                        { value: 3, label: 'ใช้จ่าย **ใกล้เคียง** กับรายรับ', apiAnswer: 'ใช้จ่ายใกล้เคียงกับรายรับ' },
                        { value: 4, label: 'ใช้จ่าย **มากกว่า** รายรับเล็กน้อย', apiAnswer: 'ใช้จ่ายมากกว่ารายรับเล็กน้อย' },
                        { value: 5, label: 'ใช้จ่าย **มากกว่า** รายรับมาก', apiAnswer: 'ใช้จ่ายมากกว่ารายรับมาก' }
                    ]
                },
                // Page 2: Question - พฤติกรรมการชำระค่าใช้จ่าย Q2
                {
                    id: 'spend_q2',
                    index: 1,
                    type: 'question',
                    category: 0,
                    categoryIcon: require('@/assets/icon/healthcheck/bbl_icons_32px_creditcard.webp'),
                    categoryTitle: 'พฤติกรรมการชำระค่าใช้จ่าย',
                    questionTitle: 'ในช่วง 1 ปีที่ผ่านมา',
                    questionSubtitle: 'ครอบครัวของคุณบริหารจัดการการชำระค่าใช้จ่ายต่างๆ ได้ดีเพียงใด',
                    notice: "โปรดเลือกข้อที่อธิบายสถานการณ์ของคุณได้ดีที่สุด",
                    choices: [
                        { value: 1, label: 'ชำระค่าใช้จ่ายทั้งหมดได้ตรงเวลา', apiAnswer: 'ชำระค่าใช้จ่ายทั้งหมดได้ตรงเวลา' },
                        { value: 2, label: 'ชำระค่าใช้จ่ายเกือบทั้งหมดได้ตรงเวลา', apiAnswer: 'ชำระค่าใช้จ่ายเกือบทั้งหมดได้ตรงเวลา' },
                        { value: 3, label: 'ชำระค่าใช้จ่ายส่วนใหญ่ได้ตรงเวลา', apiAnswer: 'ชำระค่าใช้จ่ายส่วนใหญ่ได้ตรงเวลา' },
                        { value: 4, label: 'ชำระค่าใช้จ่ายได้เพียงบางส่วนตรงเวลา', apiAnswer: 'ชำระค่าใช้จ่ายได้เพียงบางส่วนตรงเวลา' },
                        { value: 5, label: 'ชำระค่าใช้จ่ายตรงเวลาได้น้อยมาก', apiAnswer: 'ชำระค่าใช้จ่ายตรงเวลาได้น้อยมาก' }
                    ],
                    choiceFont: 'font-medium'
                },
                // Page 3: How-To - วิธีใช้จ่ายอย่างชาญฉลาด
                {
                    id: 'howto_spend',
                    index: 2,
                    type: 'howto',
                    category: 0,
                    image: require('@/assets/icon/healthcheck/image_q_3.webp'),
                    title: 'วิธีใช้จ่ายอย่างชาญฉลาด',
                    description: 'เริ่มต้นด้วยการติดตามการใช้จ่ายของตนเอง  ใช้ชีวิตให้พอดีกับฐานะ และให้ความสำคัญกับสิ่งจำเป็นเป็นอันดับแรก  ซึ่งจะช่วยให้คุณสามารถควบคุมการเงินและลดความกังวลใจลงได้'
                },
                // Page 4: Question - เงินทุนฉุกเฉิน Q1  
                {
                    id: 'save_q1',
                    index: 3,
                    type: 'question',
                    category: 1,
                    categoryIcon: require('@/assets/icon/healthcheck/savemore.webp'),
                    categoryTitle: 'เงินทุนฉุกเฉิน',
                    questionTitle: 'เงินสำรองที่คุณมีอยู่ตอนนี้',
                    questionSubtitle: 'สามารถรองรับค่าใช้จ่ายของคุณ และครอบครัวได้นานเท่าไหร่โดยไม่กระทบกับเงินออมเพื่อการเกษียณ หรือไม่ต้องกู้ยืมเพิ่ม',
                    choices: [
                        { value: 1, label: '6 เดือนหรือมากกว่า', apiAnswer: '6 เดือนหรือมากกว่า' },
                        { value: 2, label: '3-5 เดือน', apiAnswer: '3-5 เดือน' },
                        { value: 3, label: '1-2 เดือน', apiAnswer: '1-2 เดือน' },
                        { value: 4, label: '1-3 สัปดาห์', apiAnswer: '1-3 สัปดาห์' },
                        { value: 5, label: 'น้อยกว่าหนึ่งสัปดาห์', apiAnswer: 'น้อยกว่าหนึ่งสัปดาห์' }
                    ],
                    choiceFont: 'font-medium'
                },
                // Page 5: Question - การออมระยะยาว Q2 
                {
                    id: 'save_q2',
                    index: 4,
                    type: 'question',
                    category: 1,
                    categoryIcon: require('@/assets/icon/healthcheck/savemore.webp'),
                    categoryTitle: 'การออมระยะยาว',
                    questionTitle: 'เมื่อพิจารณาเป้าหมายทางการเงินระยะยาวของครอบครัว ',
                    questionSubtitle: 'ตั้งแต่การออมเพื่อการท่องเที่ยว การเริ่มต้นธุรกิจ การซื้อบ้าน ไปจนถึงแผนการเกษียณ คุณมีความเชื่อมั่นเพียงใด ว่าจะนำพาครอบครัวไปสู่เป้าหมายที่วางไว้ได้สำเร็จ',
                    choices: [
                        { value: 1, label: 'มั่นใจมาก', apiAnswer: 'มั่นใจมาก' },
                        { value: 2, label: 'มั่นใจปานกลาง', apiAnswer: 'มั่นใจปานกลาง' },
                        { value: 3, label: 'ค่อนข้างมั่นใจ', apiAnswer: 'ค่อนข้างมั่นใจ' },
                        { value: 4, label: 'มั่นใจเล็กน้อย', apiAnswer: 'มั่นใจเล็กน้อย' },
                        { value: 5, label: 'ไม่มั่นใจเลย', apiAnswer: 'ไม่มั่นใจเลย' }
                    ],
                    choiceFont: 'font-medium'
                },
                // Page 6: How-To - วิธีออมเงินให้มากขึ้น
                {
                    id: 'howto_save',
                    index: 5,
                    type: 'howto',
                    category: 1,
                    image: require('@/assets/icon/healthcheck/image_q_6.webp'),
                    title: 'วิธีออมเงินให้มากขึ้น',
                    description: 'ต้องมีการจัดสรรเงินออมอย่างสม่ำเสมอ เพื่อช่วยให้คุณพร้อมรับมือกับค่าใช้จ่ายที่ไม่คาดคิด และยังเป็นพื้นฐานในการสร้าง ความสำเร็จตามเป้าหมายระยะยาว ไม่ว่าจะเป็น เรื่องบ้าน การศึกษา ไปจนถึงการวางแผนเพื่อวัยเกษียณ'
                },
                // Page 7: Question - ภาระหนี้สินครอบครัว Q1 
                {
                    id: 'borrow_q1',
                    index: 6,
                    type: 'question',
                    category: 2,
                    categoryIcon: require('@/assets/icon/healthcheck/bbl_icons_32px_loanfinance.webp'),
                    categoryTitle: 'ภาระหนี้สินครอบครัว',
                    questionTitle: 'เมื่อพิจารณาภาระหนี้สินในปัจจุบันทั้งหมดของครอบครัวคุณ',
                    questionSubtitle: 'ไม่ว่าจะเป็นสินเชื่อบ้าน สินเชื่อส่วนบุคคล หรือยอดบัตรเครดิต',
                    notice1: "ข้อใดต่อไปนี้อธิบายความสามารถในการบริหารจัดการหนี้สินของคุณได้ดีที่สุด",
                    choices: [
                        { value: 1, label: 'ไม่มีภาระหนี้สิน', apiAnswer: 'ไม่มีภาระหนี้สิน' },
                        { value: 2, label: 'มีภาระหนี้สินในระดับที่จัดการได้', apiAnswer: 'มีภาระหนี้สินในระดับที่จัดการได้' },
                        { value: 3, label: 'มีภาระหนี้สินสูงกว่าระดับที่จัดการได้', apiAnswer: 'มีภาระหนี้สินสูงกว่าระดับที่จัดการได้' },
                        { value: 4, label: 'มีภาระหนี้สินสูงมากเกินกว่าจะจัดการไหว', apiAnswer: 'มีภาระหนี้สินสูงเกินกว่าจะจัดการไหว' }
                    ],
                    choiceFont: 'font-medium'
                },
                // Page 8: How-To - วิธีกู้ยืมอย่างชาญฉลาด
                {
                    id: 'howto_borrow',
                    index: 7,
                    type: 'howto',
                    category: 2,
                    image: require('@/assets/icon/healthcheck/image_q_8.webp'),
                    title: 'วิธีกู้ยืมอย่างชาญฉลาด',
                    description: 'การบริหารภาระหนี้สินให้อยู่ในระดับที่สามารถจัดการได้นั้น เริ่มต้นจากการกู้ยืมเท่าที่จำเป็น และสอดคล้องกับความสามารถในการชำระคืนควบคู่ไปกับการชำระคืนอย่างสม่ำเสมอ และหาแนวทางลดภาระหนี้ที่มี อัตราดอกเบี้ยสูงอย่างต่อเนื่อง'
                },
                // Page 9: Question - ความคุ้มครองจากประกัน Q1  
                {
                    id: 'plan_q1',
                    index: 8,
                    type: 'question',
                    category: 3,
                    categoryIcon: require('@/assets/icon/healthcheck/bbl_icons_32px_insurance.webp'),
                    categoryTitle: 'ความคุ้มครองจากประกัน',
                    questionTitle: 'คุณมีความมั่นใจเพียงใดว่าแผนประกันทั้งหมดที่คุณ และครอบครัวมีอยู่ ',
                    questionSubtitle: 'ไม่ว่าจะเป็นประกันสุขภาพ ประกันรถยนต์ หรือ ประกันชีวิต จะสามารถรองรับเหตุการณ์ที่ไม่คาดคิดได้',
                    notice1: "โปรดเลือกระดับความมั่นใจของคุณ",
                    choices: [
                        { value: 1, label: 'มั่นใจมาก', apiAnswer: 'มั่นใจมาก' },
                        { value: 2, label: 'มั่นใจปานกลาง', apiAnswer: 'มั่นใจปานกลาง' },
                        { value: 3, label: 'ค่อนข้างมั่นใจ', apiAnswer: 'ค่อนข้างมั่นใจ' },
                        { value: 4, label: 'มั่นใจเล็กน้อย', apiAnswer: 'มั่นใจเล็กน้อย' },
                        { value: 5, label: 'ไม่มั่นใจเลย', apiAnswer: 'ไม่มั่นใจเลย' },
                        { value: 6, label: 'ไม่มีใครในครอบครัวมีประกัน', apiAnswer: 'ไม่มีใครในครอบครัวมีประกัน' }
                    ],
                    choiceFont: 'font-medium'
                },
                // Page 10: Question - การวางแผนทางการเงิน Q2  
                {
                    id: 'plan_q2',
                    index: 9,
                    type: 'question',
                    category: 3,
                    categoryIcon: require('@/assets/icon/healthcheck/bbl_icons_32px_insurance.webp'),
                    categoryTitle: 'การวางแผนทางการเงิน',
                    questionTitle: '',
                    questionSubtitle: '"ครอบครัวของเรามีการวางแผนการเงินล่วงหน้า“ <br/><br/>',
                    notice1: "คุณเห็นด้วยกับข้อความข้างต้นมากน้อยเพียงใด",
                    choices: [
                        { value: 1, label: 'เห็นด้วยอย่างยิ่ง', apiAnswer: 'เห็นด้วยอย่างยิ่ง' },
                        { value: 2, label: 'ค่อนข้างเห็นด้วย', apiAnswer: 'ค่อนข้างเห็นด้วย' },
                        { value: 3, label: 'เฉยๆ', apiAnswer: 'เฉยๆ' },
                        { value: 4, label: 'ค่อนข้างไม่เห็นด้วย', apiAnswer: 'ค่อนข้างไม่เห็นด้วย' },
                        { value: 5, label: 'ไม่เห็นด้วยอย่างยิ่ง', apiAnswer: 'ไม่เห็นด้วยอย่างยิ่ง' }
                    ],
                    choiceFont: 'font-medium'
                },
                // Page 11: How-To - วิธีการวางแผนอย่างรอบคอบ
                {
                    id: 'howto_plan',
                    index: 10,
                    type: 'howto',
                    category: 3,
                    image: require('@/assets/icon/healthcheck/image_q_11.webp'),
                    title: 'วิธีการวางแผนอย่างรอบคอบ',
                    description: 'เริ่มต้นจากการมีความคุ้มครองประกันที่เหมาะ สม มีการตั้งเป้าหมายทางการเงินให้พร้อมสำหรับอนาคต และทุกสถานการณ์ที่ไม่คาดฝัน'
                }
            ]
        };
    },
    computed: {
        currentPage() {
            return this.pages[this.currentPageIndex];
        }
    },
    mounted() {
        // Get modal controller
        this.modal = ModalStore.methods.getModalController();
        
        // Check if test mode is enabled
        if (SurveyStatusHelper.isTestMode(this.$route)) {
            // Skip survey check in test mode
            this.isLoading = false;
            this.startTime = Date.now();
            return;
        }
        
        // Check survey status before allowing quiz
        this.checkSurveyStatus();
    },
    methods: {
        checkSurveyStatus() {
            SurveyStatusHelper.checkSurveyStatus({
                router: this.$router,
                redirectOnSubmitted: true,
                onNotSubmitted: () => {
                    // User can proceed with quiz
                    this.isLoading = false;
                    this.startTime = Date.now();
                },
                onAlreadySubmitted: () => {
                    // Will be redirected to result page
                    // Keep loading state
                },
                onError: () => {
                    // On other errors, allow user to proceed
                    this.isLoading = false;
                    this.startTime = Date.now();
                }
            });
        },
        handleAnswer(value) {
            this.answers[this.currentPage.id] = value;
        },
        handleNext(value) {
            // Save answer if it's a question page
            if (this.currentPage.type === 'question' && value) {
                this.answers[this.currentPage.id] = value;
                
                // For question pages, always send Completed: false
                this.saveAnswerLog(false); 
            }
            else {
                // Check if this is the last page (howto_plan)
                if (this.currentPageIndex >= this.pages.length - 1) {
                    // Send Completed: true on the last page before navigating to result
                    this.saveAnswerLog(true);
                    return; 
                }
            
                // Go to next page
                this.currentPageIndex++;
            } 
        },
        handleBack() {
            if (this.currentPageIndex <= 0) {
                // Go back to main page
                this.$router.push({ name: 'financial-health' });
                return;
            }
            
            // Go to previous page
            this.currentPageIndex--;
        },
        getTimeSpentSeconds() {
            if (!this.startTime) return 0;
            return Math.round((Date.now() - this.startTime) / 1000);
        },
        formatAnswersForApi() {
            const formattedAnswers = [];
            
            for (const [pageId, answerValue] of Object.entries(this.answers)) {
                const mapping = this.answerMapping[pageId];
                if (mapping) {
                    // Find the page to get the answer
                    const page = this.pages.find(p => p.id === pageId);
                    if (page && page.choices) {
                        const choice = page.choices.find(c => c.value === answerValue);
                        if (choice) {
                            // Use apiAnswer if available, otherwise use label without markdown
                            const answerText = choice.apiAnswer || choice.label.replace(/\*\*/g, '');
                            formattedAnswers.push({
                                Category: mapping.category,
                                QuestionNumber: mapping.questionNumber,
                                Answer: answerText
                            });
                        }
                    }
                }
            }
            
            return formattedAnswers;
        },
        /**
         * Save answer log - sends ALL answers from beginning to current question
         * Called when user clicks "Next" on a question page or last howto page
         * @param {boolean} isCompleted - true when submitting from the last page
         */
        saveAnswerLog(isCompleted) {
            if (this.isSubmitting) return;
            
            // Format ALL collected answers (from first question to current)
            const allAnswers = this.formatAnswersForApi();
            
            if (allAnswers.length === 0) {
                // No answers yet, just go to next page
                if (!isCompleted) {
                    this.currentPageIndex++;
                }
                return;
            }
            
            const timeSpent = this.getTimeSpentSeconds();
            
            // If completing survey, show loading state
            if (isCompleted) {
                this.isSubmitting = true;
            }
            
            // Send all answers with Completed flag
            BzbsFinancialHealth.apiSubmitSurvey(allAnswers, timeSpent, isCompleted).subscribe({
                next: (result) => {
                    // Check if response has error
                    if (result && result.requestId && !result.data) {
                        if (isCompleted) {
                            this.showErrorModal('เกิดข้อผิดพลาด', result.requestId);
                            this.isSubmitting = false;
                        }
                        return;
                    }
                    
                    if (isCompleted) {
                        // Navigate to result page after completing survey
                        this.$router.push({ name: 'financial-health-result' });
                    } else {
                        // Go to next page
                        this.currentPageIndex++;
                    }
                },
                error: (err) => {
                    console.error('Error saving answer log:', err);
                    if (isCompleted) {
                        this.isSubmitting = false;
                        this.showErrorModal('เกิดข้อผิดพลาด', err.message || 'กรุณาลองใหม่อีกครั้ง');
                    } else {
                        // Even if save fails, still go to next page (non-blocking)
                        this.currentPageIndex++;
                    }
                }
            });
        },
        /*
        submitSurvey() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            
            const timeSpent = this.getTimeSpentSeconds();
            const formattedAnswers = this.formatAnswersForApi();
            
            BzbsFinancialHealth.apiSubmitSurvey(formattedAnswers, timeSpent, true).subscribe({
                next: (result) => {
                    // Check if response has error in requestId
                    if (result && result.requestId && !result.data) {
                        // Show error popup
                        this.showErrorModal('เกิดข้อผิดพลาด', result.requestId);
                        this.isSubmitting = false;
                        return;
                    }
                    // Navigate to result page
                    this.$router.push({ name: 'financial-health-result' });
                },
                error: (err) => {
                    console.error('Error submitting survey:', err);
                    this.isSubmitting = false;
                    // Show error popup
                    this.showErrorModal('เกิดข้อผิดพลาด', err.message || 'กรุณาลองใหม่อีกครั้ง');
                }
            });
        },*/
        showErrorModal(title, message) {
            if (this.modal && this.modal.errorModal) {
                this.modal.errorModal
                    .clearModal()
                    .setTitle(title)
                    .setMsg(message)
                    .setBtnOk('ตกลง')
                    .show();
            } else {
                // Fallback to alert if modal not available
                alert(message);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style>
